import { ContentWrapper, ErrorFallback, ModalDialog, SuspenseFallback } from '@components'
import MaintenanceModal from '@components/MaintenanceModal'
import { ReportProblem } from '@views/ReportProblem'
import { configuration, useLogErrorRequest } from 'mmfintech-checkout-commons'
import { GlobalContextProvider } from 'mmfintech-commons'
import { Suspense, useEffect, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { withTranslation } from 'react-i18next'
import { Redirect, Route, type RouteProps, BrowserRouter as Router, Switch } from 'react-router-dom'
import { ThemeProvider } from 'styled-components'
import routes from './routes'
import settings, { updateDocumentHead } from './settings'
import themeBuilder from './themes'

const AppInner = () => {
  const [modalVisible, setModalVisible] = useState(false)

  const modalHide = () => {
    setModalVisible(false)
  }

  const modalShow = () => {
    setModalVisible(true)
  }

  const globalContext = { modalHide, modalShow }

  useEffect(() => {
    updateDocumentHead()
    configuration.createBackendConfig(settings.backendForLocalhost)
    // configuration.createCardGatewayConfig(settings.)
    // eslint-disable-next-line
  }, [])

  return (
    <ThemeProvider theme={themeBuilder()}>
      <GlobalContextProvider context={globalContext}>
        <Router>
          <ContentWrapper>
            <Switch>
              {routes.map(route => {
                const { path, exact, redirect, component } = route

                if (redirect) {
                  return (
                    <Route key={route.path} exact={exact} path={path}>
                      <Redirect to={redirect} />
                    </Route>
                  )
                }

                if (component) {
                  return <CustomRoute key={route.path} path={path} exact={exact} component={component} />
                }

                return null
              })}
            </Switch>

            <ModalDialog
              content={<ReportProblem visible={modalVisible} onClose={() => setModalVisible(false)} />}
              visible={modalVisible}
              onClose={() => setModalVisible(false)}
              options={{ closeOnClickOutside: false }}
            />

            <MaintenanceModal />
          </ContentWrapper>
        </Router>
      </GlobalContextProvider>
    </ThemeProvider>
  )
}

const ThisApp = withTranslation()(AppInner)

const App = () => {
  const { logErrorRequest } = useLogErrorRequest()

  const handleError = (error: Error, info: { componentStack: string }) => {
    logErrorRequest(error.toString(), info?.componentStack)
  }

  const handleReset = () => {
    window.location.replace('/')
  }

  return (
    <Suspense fallback={<SuspenseFallback />}>
      <ErrorBoundary FallbackComponent={ErrorFallback} onError={handleError} onReset={handleReset}>
        <ThisApp />
      </ErrorBoundary>
    </Suspense>
  )
}

export default App

const CustomRoute = ({ component: Component, ...rest }: RouteProps) => (
  <Route {...rest} render={props => <Component {...props} />} />
)
